import React, { useEffect, useRef, useState } from "react";
import {
  Scale,
  ShieldCheck,
  FolderKanban,
  BarChart3,
  Briefcase,
  Calculator,
  CircuitBoard,
  Percent,
  Building2,
  Globe2,
} from "lucide-react";

/*
 * Orbital login ecosystem — pure visual layer.
 * ------------------------------------------------------------------
 * • White background with a subtle #f0f2f8 radial glow behind the orb
 * • Central glassmorphism orb with a thin gold accent ring
 * • Three concentric elliptical orbit rings + soft wave lines (canvas)
 * • Ten service-node pills evenly around the outermost ring
 * • Dashed connector lines from every node → orb (canvas), with a
 *   continuous particle travelling toward the centre along each one
 * • Whole node group rotates around the orb over 40s (nodes remain
 *   upright); pauses on hover, on `prefers-reduced-motion`, and below
 *   768px viewport width
 */

const SERVICES = [
  { label: "Litigation Management", icon: Scale },
  { label: "Compliances Management", icon: ShieldCheck },
  { label: "Documentary Repository", icon: FolderKanban },
  { label: "Data Analytics", icon: BarChart3 },
  { label: "Secretarial Compliances", icon: Briefcase },
  { label: "Accounting", icon: Calculator },
  { label: "Technology Tools", icon: CircuitBoard },
  { label: "Tax Reconciliations", icon: Percent },
  { label: "MSME Compliances", icon: Building2 },
  { label: "FCRA Compliances", icon: Globe2 },
];

// Palette per spec
const C = {
  brand: "#6b6bb5",
  indigo: "#2d2d7a",
  gold: "#b8860b",
  navy: "#1a1a2e",
  ring: "#dde2f0",
  softBlue: "#f0f2f8",
  muted: "#888888",
};

export default function LoginOrbital() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    angle: 0,          // rotation offset (radians)
    lastTs: 0,
    hoveredIdx: -1,
    particles: [],     // {idx, t}
    reduced: false,
    small: false,
    size: { w: 0, h: 0, cx: 0, cy: 0, Rx: 0, Ry: 0 },
    ringsOpacity: 0,
    orbOpacity: 0,
    nodesOpacity: 0,
  });
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [tick, setTick] = useState(0); // used to trigger re-render for node positions

  // -----------------------------------------------------------------
  // Compute node & connector geometry
  // -----------------------------------------------------------------
  const computePositions = (s, angleOffset) => {
    const { cx, cy, Rx, Ry } = s.size;
    return SERVICES.map((_, i) => {
      const a = angleOffset + (i / SERVICES.length) * Math.PI * 2;
      const x = cx + Rx * Math.cos(a);
      const y = cy + Ry * Math.sin(a);
      return { x, y, a };
    });
  };

  // -----------------------------------------------------------------
  // Canvas drawing
  // -----------------------------------------------------------------
  const draw = (ctx, s, dt) => {
    const { w, h, cx, cy, Rx, Ry } = s.size;
    ctx.clearRect(0, 0, w, h);

    // Soft wave lines in far background (8% opacity)
    ctx.save();
    ctx.globalAlpha = 0.08 * s.ringsOpacity;
    ctx.strokeStyle = C.brand;
    ctx.lineWidth = 1;
    const waveTime = (performance.now() / 3000);
    for (let k = 0; k < 3; k++) {
      ctx.beginPath();
      const baseY = cy - 220 + k * 220;
      for (let x = 0; x <= w; x += 8) {
        const y = baseY + Math.sin(x * 0.008 + waveTime + k) * 22;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();

    // Three concentric elliptical orbit rings
    const ringSpecs = [
      { scale: 0.55, alpha: 0.20 },
      { scale: 0.78, alpha: 0.35 },
      { scale: 1.00, alpha: 0.50 },
    ];
    ctx.save();
    ctx.strokeStyle = C.brand;
    ctx.lineWidth = 1;
    ringSpecs.forEach((r) => {
      ctx.globalAlpha = r.alpha * s.ringsOpacity;
      ctx.beginPath();
      ctx.ellipse(cx, cy, Rx * r.scale, Ry * r.scale, 0, 0, Math.PI * 2);
      ctx.stroke();
    });
    ctx.restore();

    // Scatter pulsing dots along each ring (not the outermost — those
    // carry service nodes).
    const t = performance.now() / 1000;
    ringSpecs.forEach((r, ri) => {
      if (ri === ringSpecs.length - 1) return; // skip outermost
      const count = 10 + ri * 6;
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + (ri === 1 ? 0.15 : 0) + s.angle * (ri === 0 ? -0.4 : 0.25);
        const px = cx + Rx * r.scale * Math.cos(a);
        const py = cy + Ry * r.scale * Math.sin(a);
        const pulse = 0.8 + 0.2 * (0.5 + 0.5 * Math.sin(t * 1.2 + i * 0.7 + ri));
        const isGold = i % 9 === 0;
        const isIndigo = i % 3 === 0;
        ctx.beginPath();
        ctx.fillStyle = isGold ? C.gold : isIndigo ? C.indigo : C.brand;
        ctx.globalAlpha = 0.9 * s.ringsOpacity;
        ctx.arc(px, py, 2.2 * pulse, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.globalAlpha = 1;

    // Connector lines from each service node to orb edge
    const positions = computePositions(s, s.angle);
    const orbR = Math.min(Rx, Ry) * 0.38; // matches CSS orb radius approx
    ctx.save();
    ctx.setLineDash([5, 6]);
    ctx.lineWidth = 1;
    positions.forEach((p, i) => {
      const hovered = i === s.hoveredIdx;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const dist = Math.hypot(dx, dy);
      const ux = dx / dist;
      const uy = dy / dist;
      // start at orb edge, end 16px before the node centre
      const startX = cx + ux * (orbR + 8);
      const startY = cy + uy * (orbR + 8);
      const endX = p.x - ux * 22;
      const endY = p.y - uy * 22;
      ctx.strokeStyle = C.brand;
      ctx.globalAlpha =
        (hovered ? 1 : 0.4) * s.nodesOpacity;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      // dot where connector meets outermost ring
      const meetX = cx + ux * Rx;
      const meetY = cy + uy * Ry;
      ctx.globalAlpha = 0.9 * s.nodesOpacity;
      ctx.fillStyle = C.brand;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(meetX, meetY, 2.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.setLineDash([5, 6]);
    });
    ctx.restore();

    // Particles travelling from each node → orb centre
    ctx.save();
    s.particles.forEach((pt) => {
      const p = positions[pt.idx];
      const dx = cx - p.x;
      const dy = cy - p.y;
      const px = p.x + dx * pt.t;
      const py = p.y + dy * pt.t;
      const fade = pt.t < 0.85 ? 1 : Math.max(0, 1 - (pt.t - 0.85) / 0.15);
      ctx.globalAlpha = 0.9 * fade * s.nodesOpacity;
      ctx.fillStyle = pt.idx === s.hoveredIdx ? C.gold : C.indigo;
      ctx.beginPath();
      ctx.arc(px, py, 2.2, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
    ctx.globalAlpha = 1;
  };

  // -----------------------------------------------------------------
  // Animation loop
  // -----------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const s = stateRef.current;
    s.reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    s.small = window.innerWidth < 768;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.size = {
        w: rect.width,
        h: rect.height,
        cx: rect.width * 0.5,
        cy: rect.height * 0.5,
        Rx: Math.min(rect.width, rect.height) * 0.42,
        Ry: Math.min(rect.width, rect.height) * 0.4,
      };
    };
    resize();
    window.addEventListener("resize", resize);

    // Fade-in phases
    const start = performance.now();
    const step = (now) => {
      const ctx = canvas.getContext("2d");
      const dt = s.lastTs ? (now - s.lastTs) / 1000 : 0;
      s.lastTs = now;

      const elapsed = (now - start) / 1000;
      // Phase timings — rings 0.4s → 1.2s, orb 1.2s → 1.8s, nodes fade begins 1.8s
      s.ringsOpacity = Math.min(1, Math.max(0, (elapsed - 0.4) / 0.8));
      s.orbOpacity = Math.min(1, Math.max(0, (elapsed - 1.2) / 0.6));
      s.nodesOpacity = Math.min(1, Math.max(0, (elapsed - 1.8) / 0.8));

      // Rotate the whole group unless paused
      const rotationEnabled = !s.reduced && !s.small && s.hoveredIdx < 0;
      if (rotationEnabled) {
        s.angle += (Math.PI * 2 / 40) * dt; // full turn = 40s
      }

      // Manage particles: spawn one per connector when none active
      if (!s.reduced && s.nodesOpacity > 0.1) {
        for (let i = 0; i < SERVICES.length; i++) {
          if (!s.particles.some((p) => p.idx === i)) {
            s.particles.push({ idx: i, t: -0.05 * Math.random() - i * 0.02 });
          }
        }
        s.particles = s.particles
          .map((p) => ({ ...p, t: p.t + dt * 0.55 }))
          .filter((p) => p.t < 1.05);
      } else {
        s.particles = [];
      }

      draw(ctx, s, dt);
      // Trigger React re-render each frame so node positions update
      // (this is cheap because <ServiceNode> is memoised implicitly by React).
      setTick((v) => (v + 1) % 100000);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Sync React state → ref
  useEffect(() => {
    stateRef.current.hoveredIdx = hoverIdx;
  }, [hoverIdx]);

  const s = stateRef.current;
  const positions = s.size.Rx ? computePositions(s, s.angle) : [];

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full overflow-hidden"
      style={{
        background: `radial-gradient(60% 60% at 50% 50%, ${C.softBlue} 0%, #ffffff 65%)`,
      }}
      data-testid="login-orbital"
    >
      <canvas ref={canvasRef} className="absolute inset-0" aria-hidden="true" />

      {/* Central orb */}
      <div
        className="absolute pointer-events-none select-none"
        style={{
          left: s.size.cx || "50%",
          top: s.size.cy || "50%",
          transform: "translate(-50%, -50%)",
          opacity: s.orbOpacity,
          transition: "opacity 0.4s ease-out",
        }}
      >
        <CentralOrb reduced={s.reduced} />
      </div>

      {/* Ten service node pills, absolutely positioned via computed geometry */}
      {positions.map((p, i) => (
        <ServiceNode
          key={i}
          idx={i}
          x={p.x}
          y={p.y}
          data={SERVICES[i]}
          hovered={hoverIdx === i}
          onEnter={() => setHoverIdx(i)}
          onLeave={() => setHoverIdx(-1)}
          opacity={s.nodesOpacity}
        />
      ))}
    </div>
  );
}

// -------------------------------------------------------------------
// Central orb (glassmorphic, gold ring, brand text)
// -------------------------------------------------------------------
function CentralOrb() {
  const size = 320;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background:
          "radial-gradient(circle at 32% 30%, rgba(255,255,255,0.95) 0%, rgba(240,242,248,0.85) 45%, rgba(107,107,181,0.10) 100%)",
        boxShadow:
          "inset 0 0 60px rgba(107,107,181,0.18), 0 20px 60px -20px rgba(45,45,122,0.20)",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="login-orb"
    >
      {/* Inner glow */}
      <div
        style={{
          position: "absolute",
          inset: 8,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 50% 40%, rgba(107,107,181,0.15) 0%, rgba(107,107,181,0) 70%)",
        }}
      />
      {/* Gold accent ring */}
      <div
        style={{
          position: "absolute",
          inset: -6,
          borderRadius: "50%",
          border: `1px solid ${C.gold}`,
          opacity: 0.6,
          pointerEvents: "none",
        }}
      />
      <div
        className="relative text-center px-6"
        style={{ color: C.navy, lineHeight: 1.1 }}
      >
        <div style={{ fontSize: 52, fontWeight: 700, letterSpacing: "-0.02em" }}>
          NDM
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            marginTop: 6,
          }}
        >
          Compliance Tools
        </div>
        <div
          style={{
            marginTop: 10,
            fontStyle: "italic",
            fontSize: 14,
            color: C.brand,
            fontWeight: 400,
          }}
        >
          Intelligence. Automation. Assurance.
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Service node pill
// -------------------------------------------------------------------
function ServiceNode({ idx, x, y, data, hovered, onEnter, onLeave, opacity }) {
  const Icon = data.icon;
  const entrance = 0.15 * idx; // 0.15s stagger
  return (
    <div
      className="absolute"
      style={{
        left: x,
        top: y,
        transform: `translate(-50%, -50%) scale(${hovered ? 1.05 : 1})`,
        transition: "transform 0.18s ease-out, box-shadow 0.18s ease-out",
        opacity: opacity,
        animation: `login-node-in 0.6s ease-out ${entrance}s both`,
        zIndex: hovered ? 5 : 2,
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      tabIndex={0}
      data-testid={`login-service-node-${idx}`}
    >
      <div
        className="inline-flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-full bg-white cursor-default"
        style={{
          border: `1px solid ${hovered ? C.brand : C.ring}`,
          boxShadow: hovered
            ? "0 6px 20px rgba(107,107,181,0.28)"
            : "0 4px 16px rgba(107,107,181,0.12)",
        }}
      >
        <span
          className="h-6 w-6 rounded-md inline-flex items-center justify-center shrink-0"
          style={{
            background: hovered ? C.indigo : "rgba(45,45,122,0.08)",
            color: hovered ? "#fff" : C.indigo,
            transition: "background 0.18s, color 0.18s",
          }}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.navy,
            letterSpacing: "0.005em",
          }}
        >
          {data.label}
        </span>
      </div>
    </div>
  );
}
